import React, { useCallback, useEffect, useState } from "react";
import Swal from "sweetalert2";
import he from "he";
import "./editor.css";
import ChallengeImage from "../../components/ChallengeImage";
import StageTabs, { useAdminTheme } from "../../components/StageTabs";
import {
  deleteQuestion,
  exportQuestionsCSV,
  getAdminQuestions,
  importQuestionsCSV,
  saveQuestion,
} from "../../functions/getAdminQuestions";

const MAX_QUESTION_LEN = 350;
const PAGE_SIZE = 10;

/** Page numbers with "..." gaps, e.g. 1 ... 4 5 6 ... 12 */
function pageItems(page, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const items = [1];
  const start = Math.max(2, page - 1);
  const end = Math.min(total - 1, page + 1);
  if (start > 2) items.push("...");
  for (let i = start; i <= end; i++) items.push(i);
  if (end < total - 1) items.push("...");
  items.push(total);
  return items;
}

/** Stage 1 — "Think Fast" heading, number of tasks and the question bank (editor.php). */
export default function Editor() {
  const { adminToken, currentTheme, data, save } = useAdminTheme();

  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [title, setTitle] = useState("");
  const [total, setTotal] = useState("");

  const [modal, setModal] = useState(null); // null | { mode: 'add' | 'edit', id }
  const [question, setQuestion] = useState("");
  const [saving, setSaving] = useState(false);

  const [csvOpen, setCsvOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvBusy, setCsvBusy] = useState(false);

  const load = useCallback(async () => {
    if (!currentTheme || !adminToken) return;
    setLoading(true);
    try {
      const json = await getAdminQuestions({ token: adminToken, themeName: currentTheme });
      setQuestions(json.questions || []);
    } catch (e) {
      Swal.fire("Error", e.message, "error");
    } finally {
      setLoading(false);
    }
  }, [currentTheme, adminToken]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!data) return;
    setTitle(String(data.landing_page_title1 ?? ""));
    const n = parseInt(data.questions_01, 10);
    setTotal(n > 0 ? String(n) : "");
  }, [data]);

  const totalPages = Math.max(1, Math.ceil(questions.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const visible = questions.slice(pageStart, pageStart + PAGE_SIZE);

  const onSaveSettings = async () => {
    const n = parseInt(total, 10);
    let problem = "";
    if (title.trim().length === 0 || title.length > 150) {
      problem = title.trim().length === 0 ? "The title cannot be empty or contain only spaces." : "You exceed the maximum limit of the title.";
    } else if (String(total).trim() === "") problem = "Please enter total questions.";
    else if (Number.isNaN(n) || n < 1) problem = "Total questions must be at least 1.";
    else if (n > 10) problem = "Total questions cannot be more than 10.";
    else if (n > questions.length) problem = `You cannot enter more than ${questions.length} questions.`;
    if (problem) return Swal.fire({ icon: "error", title: "Validation Error", text: problem });

    try {
      await save({ questions_01: n, landing_page_title1: title });
      Swal.fire({ title: "Data Updated", icon: "success", timer: 1000, showConfirmButton: false });
    } catch (e) {
      Swal.fire("Error", e.message, "error");
    }
  };

  const openAdd = () => {
    setModal({ mode: "add" });
    setQuestion("");
  };

  const openEdit = (q) => {
    setModal({ mode: "edit", id: q.id });
    setQuestion(he.decode(q.question_name));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const q = question.trim();
    if (!q) return Swal.fire("Question cannot be empty!", "", "error");
    if (q.length > MAX_QUESTION_LEN) return Swal.fire(`Question must be ${MAX_QUESTION_LEN} characters or less!`, "", "error");

    const formData = new FormData();
    formData.append("themeName", currentTheme);
    formData.append("action", modal.mode);
    formData.append("edit_id", modal.id || 0);
    formData.append("question", q);

    setSaving(true);
    try {
      await saveQuestion({ token: adminToken, formData });
      setModal(null);
      await Swal.fire({ title: "Question Added Successfully!", icon: "success", timer: 1500, showConfirmButton: false });
      load();
    } catch (err) {
      Swal.fire(err.message, "", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (q) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Are you sure you want to delete this item?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
    });
    if (!result.isConfirmed) return;
    try {
      await deleteQuestion({ token: adminToken, id: q.id });
      load();
    } catch (err) {
      Swal.fire("Error", err.message, "error");
    }
  };

  const handleDownload = async () => {
    try {
      const json = await exportQuestionsCSV({ token: adminToken, themeName: currentTheme });
      const url = URL.createObjectURL(new Blob([json.csv], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "questions.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      Swal.fire("Error", err.message, "error");
    }
  };

  const handleImport = async (e) => {
    e.preventDefault();
    if (!csvFile) return Swal.fire("Please choose a CSV file", "", "error");
    const formData = new FormData();
    formData.append("themeName", currentTheme);
    formData.append("file", csvFile);
    setCsvBusy(true);
    try {
      await importQuestionsCSV({ token: adminToken, formData });
      setCsvOpen(false);
      setCsvFile(null);
      await Swal.fire("SuccessFully Updated", "SuccessFully Updated All Fields", "success");
      load();
    } catch (err) {
      Swal.fire("Error", err.message, "error");
    } finally {
      setCsvBusy(false);
    }
  };

  if (!currentTheme) {
    return (
      <>
        <StageTabs />
        <div className="qe-empty">No theme selected.</div>
      </>
    );
  }

  return (
    <>
      <StageTabs />

      <div className="bp-card">
        <div className="bp-row">
          <div className="bp-col">
            <h4>Add Custom Heading Title For Challenge 1:</h4>
            <textarea className="bp-textarea" maxLength={150} value={title} onChange={(e) => setTitle(e.target.value)} />
            <p className="bp-note">* This title will reflect on the Challenge 1 of game page &nbsp; * Character limit of text is 150</p>
          </div>
          <div className="bp-col">
            <h4>Total Questions</h4>
            <input
              type="number"
              className="bp-input"
              min="1"
              max="10"
              value={total}
              onChange={(e) => setTotal(e.target.value)}
            />
            <p className="bp-note">* Total number of questions to be displayed</p>
          </div>
        </div>
        <div className="bp-actions">
          <button type="button" className="bp-btn" onClick={onSaveSettings}>
            Save
          </button>
        </div>
      </div>

      <ChallengeImage stage={1} />

      <div className="qe-page">
        <div className="qe-toolbar">
          <div className="qe-actions">
            <button type="button" className="qe-btn" onClick={openAdd}>
              <i className="fa-solid fa-plus" /> Add Question
            </button>
            <button type="button" className="qe-btn" onClick={() => setCsvOpen(true)}>
              <i className="fa-solid fa-upload" /> Upload CSV
            </button>
            <button type="button" className="qe-btn" onClick={handleDownload}>
              <i className="fa-solid fa-download" /> Export CSV
            </button>
            <a className="qe-btn" href="/sample_bigpicture.csv" download>
              <i className="fa-solid fa-file-csv" /> Sample CSV
            </a>
          </div>
        </div>

        <div className="qe-card">
          <table className="qe-table">
            <thead>
              <tr>
                <th>No</th>
                <th>Question Name</th>
                <th>Edit</th>
                <th>Delete</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={4}>Loading...</td>
                </tr>
              )}
              {!loading && questions.length === 0 && (
                <tr>
                  <td colSpan={4}>No questions yet. Add a question or upload a CSV.</td>
                </tr>
              )}
              {!loading &&
                visible.map((q, i) => (
                  <tr key={q.id}>
                    <td>{pageStart + i + 1}</td>
                    <td className="qe-left">{he.decode(q.question_name)}</td>
                    <td>
                      <button type="button" className="qe-btn qe-small" onClick={() => openEdit(q)}>
                        <i className="fa-solid fa-pen-to-square" /> Edit
                      </button>
                    </td>
                    <td>
                      <button type="button" className="qe-btn qe-small qe-danger" onClick={() => handleDelete(q)}>
                        <i className="fa-solid fa-trash" /> Delete
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {questions.length > PAGE_SIZE && (
          <div className="qe-pagination">
            <button type="button" className="qe-pgbtn" disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)}>
              Prev
            </button>
            {pageItems(currentPage, totalPages).map((it, i) =>
              it === "..." ? (
                <span key={`gap${i}`} className="qe-gap">
                  ...
                </span>
              ) : (
                <button
                  key={it}
                  type="button"
                  className={`qe-pgbtn${it === currentPage ? " active" : ""}`}
                  onClick={() => setPage(it)}
                >
                  {it}
                </button>
              )
            )}
            <button
              type="button"
              className="qe-pgbtn"
              disabled={currentPage === totalPages}
              onClick={() => setPage(currentPage + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {modal && (
        <div className="qe-backdrop" onClick={() => setModal(null)}>
          <form className="qe-modal" onClick={(e) => e.stopPropagation()} onSubmit={handleSave}>
            <h4>{modal.mode === "edit" ? "Edit Question" : "New Question"}</h4>
            <label htmlFor="qe-question">Question</label>
            <textarea
              id="qe-question"
              maxLength={MAX_QUESTION_LEN}
              placeholder="Question"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
            />
            <div className="qe-note">Max length {MAX_QUESTION_LEN} characters</div>
            <div className="qe-modal-actions">
              <button type="button" className="qe-btn qe-secondary" onClick={() => setModal(null)}>
                Cancel
              </button>
              <button type="submit" className="qe-btn" disabled={saving}>
                {saving ? "Saving..." : modal.mode === "edit" ? "Update" : "Add New Question"}
              </button>
            </div>
          </form>
        </div>
      )}

      {csvOpen && (
        <div className="qe-backdrop" onClick={() => setCsvOpen(false)}>
          <form className="qe-modal" onClick={(e) => e.stopPropagation()} onSubmit={handleImport}>
            <h4>Upload CSV</h4>
            <input type="file" accept=".csv" onChange={(e) => setCsvFile(e.target.files[0] || null)} />
            <p className="qe-note">
              * Questions will be overwritten.
              <br />* Use only CSV files.
              <br />* Whitespace will be removed automatically.
              <br />* Check the sample CSV for the correct format (one column: <code>question_name</code>).
            </p>
            <div className="qe-modal-actions">
              <button type="button" className="qe-btn qe-secondary" onClick={() => setCsvOpen(false)}>
                Close
              </button>
              <button type="submit" className="qe-btn" disabled={csvBusy}>
                {csvBusy ? "Uploading..." : "Upload"}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
